import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { 
  LayoutDashboard, 
  Megaphone, 
  BarChart3, 
  Users, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  ChevronDown,
  Bell,
  Search,
  Shield,
  Building,
  Coins,
  AlertCircle
} from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { useAdminAuth } from '../../hooks/useAdminAuth'
import { Button } from '../common/UI'

interface SidebarItemProps {
  icon: React.ComponentType<{ className?: string }>
  label: string
  path: string
  active?: boolean
  onClick?: () => void
}

const SidebarItem: React.FC<SidebarItemProps> = ({ icon: Icon, label, path, active, onClick }) => {
  return (
    <Link
      to={path}
      onClick={onClick}
      className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
        active
          ? 'bg-primary text-white'
          : 'text-text-secondary hover:bg-background-gray hover:text-text-primary'
      }`}
    >
      <Icon className="w-5 h-5 mr-3" />
      {label}
    </Link>
  )
}

interface MerchantLayoutProps {
  children: React.ReactNode
}

export const MerchantLayout: React.FC<MerchantLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { user, signOut } = useAuth()

  const merchantNavigation = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/merchant/dashboard' },
    { icon: Building, label: 'Businesses', path: '/merchant/businesses' },
    { icon: Megaphone, label: 'Advertisements', path: '/merchant/advertisements' },
    { icon: Coins, label: 'Credits', path: '/merchant/credits' },
    { icon: Bell, label: 'Notifications', path: '/merchant/notifications' },
    { icon: Settings, label: 'Profile', path: '/merchant/profile' }
  ]

  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }

  return (
    <div className="flex h-screen bg-background-gray">
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}>
        <div className="flex items-center justify-between h-16 px-4 border-b border-border-gray">
          <Link to="/merchant/dashboard" className="flex items-center">
            <img 
              src="/assets/logo.png" 
              alt="BlastTime Logo"
              className="w-8 h-8"
            />
            <span className="ml-2 text-lg font-semibold text-text-primary">Merchant</span>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden"
            title="Close sidebar"
          >
            <X className="w-6 h-6 text-text-muted" />
          </button>
        </div>

        <nav className="mt-8 px-4 space-y-2">
          {merchantNavigation.map((item) => (
            <SidebarItem
              key={item.path}
              icon={item.icon}
              label={item.label}
              path={item.path}
              active={location.pathname === item.path}
              onClick={() => setSidebarOpen(false)}
            />
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border-gray">
          <Button
            variant="ghost"
            className="w-full justify-start"
            icon={LogOut}
            onClick={handleSignOut}
          >
            Sign Out
          </Button>
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow-sm border-b border-border-gray">
          <div className="flex items-center justify-between h-16 px-4 lg:px-6">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden mr-4"
                title="Open sidebar"
              >
                <Menu className="w-6 h-6 text-text-muted" />
              </button>
              
              <div className="hidden md:flex items-center max-w-md w-full">
                <div className="relative w-full">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search..."
                    className="w-full pl-10 pr-4 py-2 border border-border-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <button
                className="p-2 text-text-muted hover:text-text-primary rounded-lg hover:bg-background-gray"
                title="Notifications"
                aria-label="Notifications"
              >
                <Bell className="w-5 h-5" />
              </button>

              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-background-gray"
                >
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {user?.email?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="hidden md:block text-sm font-medium text-text-primary">
                    {user?.email}
                  </span>
                  <ChevronDown className="w-4 h-4 text-text-muted" />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-border-gray z-10">
                    <div className="py-1">
                      <Link
                        to="/merchant/profile"
                        className="block px-4 py-2 text-sm text-text-primary hover:bg-background-gray"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        Profile Settings
                      </Link>
                      <Link
                        to="/merchant/credits"
                        className="block px-4 py-2 text-sm text-text-primary hover:bg-background-gray"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        Credits
                      </Link>
                      <hr className="my-1 border-border-gray" />
                      <button
                        onClick={handleSignOut}
                        className="block w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-background-gray"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {children}
        </main>
      </div>

      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  )
}

interface AdminLayoutProps {
  children: React.ReactNode
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { admin, signOut } = useAdminAuth()

  const adminNavigation = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard', roles: ['SUPER_ADMIN', 'ADMIN', 'MODERATOR'] },
    { icon: Shield, label: 'Admin Management', path: '/admin/admins', roles: ['SUPER_ADMIN'] },
    { icon: Users, label: 'App Users', path: '/admin/users', roles: ['SUPER_ADMIN', 'ADMIN', 'MODERATOR'] },
    { icon: Users, label: 'Merchants', path: '/admin/merchants', roles: ['SUPER_ADMIN', 'ADMIN', 'MODERATOR'] },
    { icon: Building, label: 'Businesses', path: '/admin/companies', roles: ['SUPER_ADMIN', 'ADMIN', 'MODERATOR'] },
    { icon: Megaphone, label: 'Advertisements', path: '/admin/ads', roles: ['SUPER_ADMIN', 'ADMIN'] },
    { icon: AlertCircle, label: 'Incidents', path: '/admin/incidents', roles: ['SUPER_ADMIN', 'ADMIN', 'MODERATOR'] },
    { icon: Bell, label: 'Notifications', path: '/admin/notifications', roles: ['SUPER_ADMIN', 'ADMIN'] },
    { icon: BarChart3, label: 'Analytics', path: '/admin/analytics', roles: ['SUPER_ADMIN', 'ADMIN', 'MODERATOR'] }
  ]

  const filteredNavigation = adminNavigation.filter(item => 
    admin && item.roles.includes(admin.role)
  )

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return 'bg-purple-100 text-purple-800'
      case 'ADMIN':
        return 'bg-blue-100 text-blue-800'
      case 'MODERATOR':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/admin')
  }

  return (
    <div className="flex h-screen bg-background-gray">
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}>
        <div className="flex items-center justify-between h-16 px-4 border-b border-border-gray">
          <Link to="/admin/dashboard" className="flex items-center">
            <img 
              src="/assets/logo.png" 
              alt="BlastTime Logo"
              className="w-8 h-8"
            />
            <span className="ml-2 text-lg font-semibold text-text-primary">Admin</span>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden"
            title="Close sidebar"
          >
            <X className="w-6 h-6 text-text-muted" />
          </button>
        </div>

        <nav className="mt-8 px-4 space-y-2">
          {filteredNavigation.map((item) => (
            <SidebarItem
              key={item.path}
              icon={item.icon}
              label={item.label}
              path={item.path}
              active={location.pathname === item.path}
              onClick={() => setSidebarOpen(false)}
            />
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border-gray">
          <Button
            variant="ghost"
            className="w-full justify-start"
            icon={LogOut}
            onClick={handleSignOut}
          >
            Sign Out
          </Button>
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow-sm border-b border-border-gray">
          <div className="flex items-center justify-between h-16 px-4 lg:px-6">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden mr-4"
                title="Open sidebar"
                aria-label="Open sidebar"
              >
                <Menu className="w-6 h-6 text-text-muted" />
              </button>
              
              <h1 className="text-xl font-semibold text-text-primary">
                BlastTime Admin
              </h1>
            </div>

            <div className="flex items-center space-x-4">
              {admin && (
                <div className="hidden md:flex items-center space-x-3">
                  <div className="text-right">
                    <p className="text-sm font-medium text-text-primary">
                      {admin.firstName} {admin.lastName}
                    </p>
                    <p className={`text-xs px-2 py-0.5 rounded-full inline-block ${getRoleBadgeColor(admin.role)}`}>
                      {admin.role.replace('_', ' ')}
                    </p>
                  </div>
                </div>
              )}

              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-background-gray"
                >
                  <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {admin?.email?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="hidden md:block text-sm font-medium text-text-primary">
                    {admin?.firstName || 'Admin'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-text-muted" />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-border-gray z-10">
                    <div className="py-1">
                      <button
                        onClick={handleSignOut}
                        className="block w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-background-gray"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {children}
        </main>
      </div>

      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  )
}

interface PublicLayoutProps {
  children: React.ReactNode
}

export const PublicLayout: React.FC<PublicLayoutProps> = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  return (
    <div className="min-h-screen bg-white">
      <nav className="bg-white shadow-sm border-b border-border-gray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="/assets/logo.png" 
                alt="BlastTime Logo"
                className="w-8 h-8"
              />
              <span className="ml-2 text-xl font-bold text-text-primary">
                BlastTime
              </span>
            </Link>

            <div className="hidden md:flex items-center space-x-8">
              <Link to="/contact" className="text-text-secondary hover:text-text-primary">
                Contact
              </Link>
              <Link to="/privacy-policy" className="text-text-secondary hover:text-text-primary">
                Privacy
              </Link>
              <Link to="/terms" className="text-text-secondary hover:text-text-primary">
                Terms
              </Link>
              <Link
                to="/merchant/login"
                className="text-text-secondary hover:text-text-primary"
              >
                Sign In
              </Link>
              <Link to="/merchant/register">
                <Button>Get Started</Button>
              </Link>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-text-muted" />
              ) : (
                <Menu className="w-6 h-6 text-text-muted" />
              )}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 border-t border-border-gray">
                <Link
                  to="/contact"
                  className="block px-3 py-2 text-base font-medium text-text-secondary hover:text-text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Contact
                </Link>
                <Link
                  to="/privacy-policy"
                  className="block px-3 py-2 text-base font-medium text-text-secondary hover:text-text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Privacy
                </Link>
                <Link
                  to="/terms"
                  className="block px-3 py-2 text-base font-medium text-text-secondary hover:text-text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Terms
                </Link>
                <Link
                  to="/merchant/login"
                  className="block px-3 py-2 text-base font-medium text-text-secondary hover:text-text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign In
                </Link>
                <div className="px-3 py-2">
                  <Link to="/merchant/register" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full">Get Started</Button>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      <main>{children}</main>

      <footer className="bg-background-gray border-t border-border-gray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="/assets/logo.png" 
                  alt="BlastTime Logo"
                  className="w-8 h-8"
                />
                <span className="ml-2 text-lg font-bold text-text-primary">
                  BlastTime
                </span>
              </div>
              <p className="text-text-secondary">
                Connect and meet with friends through real experiences and meaningful connections.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-text-primary mb-3">Product</h3>
              <ul className="space-y-2 text-text-secondary">
                <li><a href="#features" className="hover:text-text-primary">Features</a></li>
                <li><a href="#download" className="hover:text-text-primary">Download App</a></li>
                <li><Link to="/merchant/register" className="hover:text-text-primary">Business</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-text-primary mb-3">Support</h3>
              <ul className="space-y-2 text-text-secondary">
                <li><Link to="/contact" className="hover:text-text-primary">Contact</Link></li>
                <li><a href="#" className="hover:text-text-primary">Help Center</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-text-primary mb-3">Legal</h3>
              <ul className="space-y-2 text-text-secondary">
                <li><Link to="/privacy-policy" className="hover:text-text-primary">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-text-primary">Terms</Link></li>
              </ul>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-border-gray">
            <p className="text-center text-text-secondary">
              © 2025 BlastTime. All rights reserved. Powered by Xupoli Technologies Inc.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}